import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  useMediaQuery,
} from "@mui/material";
import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

const ThemeContext = createContext({});

const getMuiTheme = (theme) =>
  createTheme({
    palette: {
      mode: theme,
      primary: {
        main: "#009A9A",
      },
      secondary: {
        main: "#6c757d",
      },
    },
    typography: {
      fontFamily: "Inter",
      h1: {
        fontSize: "3.8rem",
      },
      h3: {
        fontSize: "2.5rem",
      },
      h2: {
        fontSize: "3rem",
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
      },
    },
  });

const generateColors = (muiTheme) => ({
  theme: muiTheme.palette.mode,
  border: muiTheme.palette.divider,
  shadow: muiTheme.palette.divider,
  primary: muiTheme.palette.primary.main,
  primaryDark: muiTheme.palette.primary.light,
  primaryLight: muiTheme.palette.primary.dark,
  secondary: muiTheme.palette.secondary.main,
  secondaryDark: muiTheme.palette.secondary.light,
  secondaryLight: muiTheme.palette.secondary.dark,
  warning: muiTheme.palette.warning.main,
  warningDark: muiTheme.palette.warning.light,
  warningLight: muiTheme.palette.warning.dark,
  error: muiTheme.palette.error.main,
  errorDark: muiTheme.palette.error.light,
  errorLight: muiTheme.palette.error.dark,
  info: muiTheme.palette.info.main,
  infoDark: muiTheme.palette.info.light,
  infoLight: muiTheme.palette.info.dark,
  success: muiTheme.palette.success.main,
  successDark: muiTheme.palette.success.light,
  successLight: muiTheme.palette.success.dark,
  textPrimary: muiTheme.palette.text.primary,
  textSecondary: muiTheme.palette.text.secondary,
  textDisabled: muiTheme.palette.text.disabled,
  hover: muiTheme.palette.action.hover,
  paperBg: muiTheme.palette.background.paper,
  bg: muiTheme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.16)" : "#fff",
  link: muiTheme.palette.mode === "dark" ? "#87cefa" : "#00008b",
});

const ThemeContextProvider = ({ children }) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [theme, setTheme] = useState(prefersDarkMode ? "dark" : "light");

  const muiTheme = useMemo(() => getMuiTheme(theme), [theme]);
  const themeColors = useMemo(() => generateColors(muiTheme), [muiTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        colors: themeColors,
      }}
    >
      <MuiThemeProvider theme={muiTheme}>
        <StyledThemeProvider theme={themeColors}>
          {children}
        </StyledThemeProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

const useThemeContext = (silent = false) => {
  const context = useContext(ThemeContext);
  if (!context && !silent)
    throw new Error("ThemeContext must be used within ThemeContextProvider");

  const toggleTheme = () => {
    context.setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };
  const isDarkMode = useMemo(() => context.theme === "dark", [context?.theme]);
  return { ...context, toggleTheme, isDarkMode };
};

export default useThemeContext;
export { ThemeContextProvider };
